import {
  SearchOutlined,
} from "@ant-design/icons";
import {
  Row,
  Col,
  Table,
  Empty,
  Button,
  Input,
} from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { Dropdown } from "semantic-ui-react";
import { RolesService } from "../../../api/Roles/api-roles-permisos";
import { useGestionRoles } from "../../../hooks/useGestionRoles";
import { cantidadRegistros } from "../../../utils/dataSelectOptions";
import { ConfigurarModal } from "./Configurar.view";
import { TemplateContext } from "../../../utils/template_context";
import { BreadCrumbAdmin } from "./../../../components/Administrador/Breadcrumb/Breadcrumb";
import { ReactComponent as IconFlechareg } from "../../../assets/flecha_reg.svg";
import { itemRenderTable } from "../../../utils/itemRenderTable";
import { openNotification } from "../../../utils/Notifactions";

// El buscador consulta al servidor en cada pulsacion, asi que espera a que la
// persona termine de escribir y exige unas pocas letras para no pedir media
// base de datos.
const MINIMO_CARACTERES = 3;
const ESPERA_BUSQUEDA = 500;

const usersService = new RolesService();

export const GestionRoles = () => {
  const { cantidadpaginas, cambiarCantidadPaginas } = useGestionRoles();

  const coloresEstado: any = {
    Activo: "#00904C",
    Inactivo: "#4B4B4B",
  };

  const [openModal, setOpenModal] = useState(false);
  const [datos, setDatos] = useState({});
  const configurar = (datos: any) => {
    setOpenModal(true);
    setDatos(datos);
  };

  const [users, setUsers] = useState<any>([]);
  const [filter, setFilter] = useState<string>("");
  const [buscando, setBuscando] = useState(false);

  // Antes esta pantalla pedia la lista completa de usuarios al entrar, y volvia
  // a pedirla cada vez que se vaciaba el buscador. Son miles de registros sin
  // paginar: el gateway respondia 502 o 503 y la tabla quedaba vacia. Ahora solo
  // se consulta lo que se escribe en el buscador.
  const peticionVigente = useRef(0);
  const montado = useRef(true);

  useEffect(() => {
    return () => {
      montado.current = false;
    };
  }, []);

  const buscarUsuarios = async (termino: string) => {
    const peticion = peticionVigente.current + 1;
    peticionVigente.current = peticion;
    setBuscando(true);
    try {
      const { users: encontrados } = await usersService.searchUsers(termino);
      // Una respuesta que llega tarde no debe pisar la de la busqueda actual.
      if (!montado.current || peticion !== peticionVigente.current) return;
      setUsers(encontrados ?? []);
    } catch (error) {
      if (!montado.current || peticion !== peticionVigente.current) return;
      setUsers([]);
      openNotification({
        titulo: "Error",
        descripcion:
          "No fue posible consultar los usuarios. Intenta de nuevo en un momento.",
        placement: "bottomRight",
      });
    } finally {
      if (montado.current && peticion === peticionVigente.current) {
        setBuscando(false);
      }
    }
  };

  useEffect(() => {
    const termino = filter.trim();
    if (termino.length < MINIMO_CARACTERES) {
      // Descarta lo que venga en camino de una busqueda anterior.
      peticionVigente.current += 1;
      setUsers([]);
      setBuscando(false);
      return;
    }
    const temporizador = setTimeout(
      () => buscarUsuarios(termino),
      ESPERA_BUSQUEDA
    );
    return () => clearTimeout(temporizador);
  }, [filter]);

  // Al cerrar el modal de configuracion se repite la busqueda actual, no la
  // consulta de todos los usuarios.
  const recargarUsuarios = () => {
    const termino = filter.trim();
    if (termino.length >= MINIMO_CARACTERES) buscarUsuarios(termino);
  };

  const textoTablaVacia = () => {
    if (filter.trim().length < MINIMO_CARACTERES) {
      return `Escribe al menos ${MINIMO_CARACTERES} caracteres del documento, nombre o apellido para buscar`;
    }
    if (buscando) return "Buscando...";
    return "No se encontraron usuarios con ese criterio";
  };

  const context = useContext(TemplateContext);

  const columns: any = [
    {
      title: "No.",
      // width: 100,
      dataIndex: "index",
      key: "index",
      fixed: context.device === "md" ? "left" : false,
      render: (datos: any, record: any, index: number) => (
        <div>{index + 1}</div>
      ),
    },
    {
      title: "Tipo doc.",
      // width: 100,
      fixed: context.device === "md" ? "left" : false,
      dataIndex: "tipo_identificacion",
      key: "tipo_identificacion",
    },
    {
      title: "No. Identificación",
      // width: 100,
      fixed: context.device === "md" ? "left" : false,
      dataIndex: "documento",
      key: "documento",
    },
    {
      title: "Nombres y apellidos",
      width: 120,
      render: (datos: any) => {
        return (
            <>{datos.nombres + ' ' + datos.apellidos}</>
        );
      },
    },
    {
      title: "Correo electrónico",
      width: 120,
      render: (datos: any) => {
        return (
            <>{datos.correo}</>
        );
      },
    },
    {
      title: "Rol",
      // width: 120,
      dataIndex: "role",
      key: "role",
    },
    {
      title: "Estado",
      // width: 100,
      render: (datos: any) => {
        return (
            <span
              style={{
                color: coloresEstado[datos.active ? "Activo" : "Inactivo"],
              }}
            >
              {datos.active ? "Activo" : "Inactivo"}
            </span>
        );
      },
    },
    {
      title: "Fecha actualización",
      // width: 169,
      render: (datos: any) => {
        return <>{new Date(datos.updated_at).toLocaleDateString()}</>;
      },
    },
    {
      title: () => (
        <Row className="width-100">
          <Col span={24} className="justify-content-center">
            Acciones
          </Col>
        </Row>
      ),
      width: 90,
      fixed: context.device === "lg" ? "right" : false,
      render: (datos: any) => (
          <Row className="width-100">
            <Col span={24} className="justify-content-center">
              <Button
                onClick={() => configurar(datos)}
                className="no-margin button-azul-ghost font-family-Montserrat-Medium"
              >
                Configurar
              </Button>
            </Col>
          </Row>
      ),
    },
  ];

  const routes = [{ nombre: "Administración" }, { nombre: "Roles y perfiles" }];

  return (
    <>
      <BreadCrumbAdmin routes={routes} />
      <div className="background-color-F7FBFF h-100">
        <div className="container-fluid ps-md-4 pe-md-4 main-c6">
          <div className="title_out_card font-family-Montserrat-SemiBold font-size-14px">
            Gestionar roles y perfiles
          </div>

          <div className="row-shadow" style={{ marginBottom: "6%" }}>
            <div className="header_card  p-0">
              <div className="title_in_card pt-0 font-size-14px font-color-1B1C1D font-family-Montserrat-Regular">
                Listado de usuarios
              </div>
            </div>

            <div className="col-12">
              <div className="row mt-3 mb-3">
                <div className="col-12  col-md-6">
                  <div className="row">
                    {/*<div className="col-12">
                      <div className="label_container"><span>Filtrar por palabra clave</span></div>
    </div>*/}
                    <div className="col-12">
                      <Input
                        suffix={
                          <SearchOutlined className="font-color-051923bf" />
                        }
                        placeholder="Buscar por número de documento, nombre o apellidos"
                        onInput={(e: any) => setFilter(e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6 d-flex justify-content-md-end justify-content-between align-content-center align-items-center mb-3">
                  <div className="d-flex justify-content-end">
                    <div>
                      Total:{" "}
                      <span className="font-color-F28C02">
                        {users?.length}{" "}
                      </span>
                    </div>
                  </div>
                  <div
                    className="d-flex justify-content-end registos-adminconvocatoria"
                    style={{ width: 200 }}
                  >
                    <span className="font-family-Montserrat-Regular  font-size-9px font-color-7E7E7E pe-2 d-flex justify-content-end">
                      Registros por página
                    </span>
                    <Dropdown
                      className="select-registros-adminconvocatoria no-margin justify-content-center d-flex"
                      defaultValue={cantidadpaginas}
                      options={cantidadRegistros}
                      icon={<IconFlechareg/>
                    }
                      onChange={(e, { value }) =>
                        cambiarCantidadPaginas(e, value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <Table
                    className="height-30"
                    style={{ borderTop: "1px solid #C4C4C4" }}
                    loading={buscando}
                    locale={{
                      emptyText: (
                        <Empty
                          description={textoTablaVacia()}
                          style={{ padding: "50px" }}
                        />
                      ),
                    }}
                    pagination={{
                      position: ["bottomRight"],
                      itemRender: itemRenderTable,
                      pageSize: cantidadpaginas,
                    }}
                    dataSource={users}
                    columns={columns}
                    rowKey={(registro: any) => registro.id}
                    size="small"
                    bordered={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row container-sticky-footer">
        <div className="col-6">
          {/* <Button className="button-blanco-radius">ATRÁS</Button> */}
        </div>
      </div>

      {openModal && (
        <ConfigurarModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          datos={datos}
          recargarUsuarios={recargarUsuarios}
        />
      )}
    </>
  );
};
